<script setup lang="ts">
import { useDateFormat } from '~/composables/useDateFormat'

const { getYear } = useDateFormat()
const { getMapsUrl } = useMaps()

const activeFilter = ref('Tous')
const isFilterMenuOpen = ref(false)
const filters = computed(() => {
  const list = [{ label: 'Tous', value: 'Tous' }]
  if (partners.value?.some(p => p.type === 'Company')) {
    list.push({ label: 'Studio', value: 'Company' })
  }
  if (partners.value?.some(p => p.type === 'Freelance')) {
    list.push({ label: 'Intervenant', value: 'Freelance' })
  }
  return list
})

const nowTimestamp = useState('partners-now', () => Date.now())

const { getPartners } = useHome()
const { data: partners } = await useAsyncData('home-partners', () => getPartners())

const flippedPartnerId = ref<string | null>(null)

const partnersCarousel = ref<any>(null)

const toggleFlip = (partnerId: string) => {
  if (partnersCarousel.value?.wasDragging) return
  if (flippedPartnerId.value === partnerId) {
    flippedPartnerId.value = null
  } else {
    flippedPartnerId.value = partnerId
  }
}

const isFlipped = (partnerId: string) => {
  return flippedPartnerId.value === partnerId
}

const filteredPartners = computed(() => {
  if (!partners.value) return []
  let filtered = partners.value
  if (activeFilter.value !== 'Tous') {
    filtered = partners.value.filter(p => p.type === activeFilter.value)
  }
  
  const sorted = [...filtered].sort((a, b) => {
    const pA = a.priority ?? 9999
    const pB = b.priority ?? 9999
    if (pA !== pB) return pA - pB
    
    // Fallback: Date
    const dateA = a.joinedDate ? new Date(a.joinedDate).getTime() : nowTimestamp.value
    const dateB = b.joinedDate ? new Date(b.joinedDate).getTime() : nowTimestamp.value
    return dateA - dateB
  })

  // Duplicate for infinite loop if we have items
  if (sorted.length >= 4) {
    return [...sorted, ...sorted]
  }
  return sorted
})

const getPartnerKey = (partner: any, index: number) => {
  return `${partner.id}-${index}`
}

const getIcon = (iconStr: string): [string, string] | string => {
  if (!iconStr) return ['fas', 'question']
  if (iconStr.includes(' ')) {
    const [prefix, name] = iconStr.split(' ')
    if (prefix && name) {
      return [prefix, name.replace('fa-', '')] as [string, string]
    }
  }
  return ['fas', iconStr.replace('fa-', '')] as [string, string]
}
</script>

<template>
  <section id="partners" class="py-section-py bg-background border-y border-oxo-border relative transition-colors duration-300">
    <div class="container mx-auto px-container-px text-center relative z-20">
      <UiSectionHeader 
        titlePrefix="NOS"
        titleHighlight="PARTENAIRES"
        subtitle="Ils interviennent dans nos formations, proposent des stages et recrutent nos futurs talents."
      />

      <div class="relative z-40 mb-section-header-mb max-w-md mx-auto md:max-w-none">
        <!-- Mobile Filter Dropdown Trigger -->
        <UiMobileSelect
          :model-value="activeFilter"
          @update:model-value="activeFilter = $event"
          :options="filters.map(f => f.value)"
          label="Filtrer les partenaires"
          class="md:hidden mb-4"
        />

        <!-- Desktop Filters -->
        <div class="hidden md:flex justify-center">
          <UiTabs 
            :items="filters.map(f => f.label)" 
            :model-value="filters.find(f => f.value === activeFilter)?.label || 'Tous'"
            @update:model-value="(label: string) => activeFilter = filters.find(f => f.label === label)?.value || 'Tous'"
          />
        </div>
      </div>

      <UiCarousel 
        ref="partnersCarousel"
        auto-scroll 
        infinite 
        :auto-scroll-speed="1"
        :paused="!!flippedPartnerId"
        @mouseleave="flippedPartnerId = null"
        container-tag="ul"
      >
        <li 
          v-for="(partner, index) in filteredPartners" 
          :key="getPartnerKey(partner, index)"
          class="w-[262px] md:w-[350px] snap-start list-none"
        >
          <article 
            class="group relative rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 cursor-pointer w-full h-full"
            :class="partner.type === 'Company' ? 'glass-card-studio' : 'glass-card-intervenant'"
            @click="toggleFlip(partner.id)"
          >
            <!-- Flip Container -->
            <div 
              class="relative w-full h-full transition-all duration-700 transform-style-3d"
              :class="{ 'rotate-y-180': isFlipped(partner.id) }"
            >
                <!-- FRONT FACE -->
                <div class="relative w-full h-full backface-hidden">
                  <!-- Glassmorphism Background -->
                  <div class="absolute inset-0 z-0">
                      <!-- Background Image (Studio cards) - Full coverage -->
                      <template v-if="partner.backgroundImage">
                          <div class="absolute inset-0 w-full h-full">
                              <NuxtImg loading="lazy"
                              :src="partner.backgroundImage"
                              :alt="partner.imageAlt || partner.name"
                              class="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 transition-opacity duration-500"
                              sizes="262px md:350px"
                              />
                          </div>
                          <!-- Overlay to ensure text readability -->
                          <div class="absolute inset-0 bg-linear-to-t from-black via-black/60 to-black/40"></div>
                      </template>
                      <!-- Glass overlay (only without background image) -->
                      <div v-else class="absolute inset-0 backdrop-blur-xl"></div>
                      <!-- Gradient overlay for depth -->
                      <div class="absolute inset-0 bg-linear-to-br from-white/5 via-transparent to-black/30"></div>
                      <!-- Animated glow on hover (OXO colors) -->
                      <div 
                          class="absolute inset-0 opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity duration-700"
                          :class="partner.type === 'Company' ? 'bg-linear-to-t from-oxo-yellow/15 via-transparent to-oxo-yellow/5' : 'bg-linear-to-t from-oxo-red/15 via-transparent to-oxo-red/5'"
                      ></div>
                  </div>
                  
                  
                      <!-- Content Container (Front) -->
                      <div class="relative z-10 flex flex-col items-center justify-center h-[300px] md:h-[400px] p-4 md:p-8 text-center">
                          <!-- Logo / Avatar with glass effect -->
                          <div class="relative mb-6 transform group-hover:-translate-y-2 [@media(hover:none)]:-translate-y-2 transition-transform duration-500">
                              <div 
                                  class="overflow-hidden border border-white/20 shadow-2xl bg-white/10 backdrop-blur-md flex items-center justify-center ring-2 ring-white/5 group-hover:ring-white/20 [@media(hover:none)]:ring-white/20 transition-all duration-500 p-0"
                                  :class="[
                                      partner.type === 'Company' ? 'rounded-2xl' : 'rounded-full',
                                      partner.logoFormat === 'rectangular' ? 'w-24 h-16 md:w-40 md:h-24' : 'w-20 h-20 md:w-24 md:h-24'
                                  ]"
                              >
                                  <NuxtImg loading="lazy"
                                      v-if="partner.image"
                                      :src="partner.image"
                                      :alt="partner.name"
                                      class="w-full h-full object-cover"
                                      sizes="96px md:160px"
                                  />
                                  <i v-else :class="['fas', partner.type === 'Company' ? 'fa-building' : 'fa-user', 'text-3xl text-white/30']" aria-hidden="true"></i>
                              </div>
                          </div>
                          <!-- Info -->
                          <h3 :class="partner.backgroundImage ? 'text-white' : 'text-foreground'" class="font-display font-bold md:font-black text-base md:text-3xl mb-2 md:mb-3 leading-tight drop-shadow-lg group-hover:scale-105 [@media(hover:none)]:scale-105 transition-transform duration-300 flex items-center justify-center gap-3">
                              <span 
                                  class="w-3 h-3 rounded-full shadow-lg shrink-0"
                                  :class="partner.type === 'Company' ? 'bg-oxo-yellow shadow-oxo-yellow/50' : 'bg-oxo-red shadow-oxo-red/50'"
                              ></span>
                              {{ partner.firstName ? `${partner.firstName} ${partner.name}` : partner.name }}
                          </h3>
  
                          <!-- Type Badge - OXO colors -->
                          <div class="relative">
                            <span 
                                class="text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1 md:px-4 md:py-1.5 rounded-full border backdrop-blur-md shadow-lg inline-block"
                                :class="partner.type === 'Company' 
                                ? 'bg-oxo-yellow/20 text-oxo-yellow border-oxo-yellow/30' 
                                : 'bg-oxo-red/20 text-oxo-red border-oxo-red/30'"
                            >
                                {{ partner.type === 'Company' ? 'Studio' : 'INTERVENANT·E' }}
                            </span>
                          </div>
  
                          <div class="mt-8 opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 transform translate-y-4 group-hover:translate-y-0 [@media(hover:none)]:translate-y-0 transition-all duration-500">
                              <p :class="partner.backgroundImage ? 'text-white/70' : 'text-foreground-dim'" class="text-[10px] font-bold uppercase tracking-widest flex items-center gap-2">
                                  <i class="fas fa-arrow-pointer" aria-hidden="true"></i>
                                  Cliquer pour plus d'infos
                              </p>
                          </div>
                      </div>
                  </div>
  
                <!-- BACK FACE -->
                <div 
                  class="absolute inset-0 w-full h-[300px] md:h-full backface-hidden rotate-y-180 p-3 md:p-8 flex flex-col rounded-2xl"
                  :class="partner.type === 'Company' ? 'glass-card-studio' : 'glass-card-intervenant'"
                >
                    <!-- Glass background for back -->
                    <div class="absolute inset-0 backdrop-blur-xl bg-linear-to-br from-white/5 via-transparent to-black/20 rounded-2xl border border-white/10"></div>
                    
                    
                    <div class="relative z-10 flex flex-col h-full">
                        <div class="flex items-center gap-3 mb-4 md:mb-8">
                            <div class="w-8 h-8 md:w-12 md:h-12 rounded-lg overflow-hidden border border-white/10 bg-white/5">
                               <NuxtImg loading="lazy"
                                  v-if="partner.image"
                                  :src="partner.image"
                                  :alt="partner.name"
                                  class="w-full h-full object-cover"
                                  sizes="48px"
                                />
                            </div>
                            <div>
                                <h4 class="font-display font-bold text-base md:text-lg text-foreground leading-none mb-1">{{ partner.firstName ? `${partner.firstName} ${partner.name}` : partner.name }}</h4>
                                <p class="text-[10px] font-bold uppercase tracking-widest" :class="partner.type === 'Company' ? 'text-orange-400' : 'text-red-400'">
                                    {{ partner.type === 'Company' ? 'Studio' : 'INTERVENANT·E' }}
                                </p>
                            </div>
                        </div>

  
                        <div class="space-y-2 md:space-y-4 grow text-left items-start">
                            <!-- FOR FREELANCE: Entreprises -->
                            <div v-if="partner.type === 'Freelance' && partner.companies && partner.companies.length > 0">
                                <p class="text-[10px] font-semibold uppercase tracking-wider text-foreground-dim mb-1 flex items-center gap-2">
                                    <i class="fas fa-building text-oxo-red" aria-hidden="true"></i>
                                    Entreprises
                                </p>
                                <p class="text-sm text-foreground leading-relaxed pl-5">{{ partner.companies.join(' • ') }}</p>
                            </div>

                            <!-- FOR COMPANY: Lieu avec lien Google Maps -->
                            <div v-if="partner.type === 'Company' && partner.locations && partner.locations.length > 0">
                                <p class="text-[10px] font-semibold uppercase tracking-wider text-foreground-dim mb-1 flex items-center gap-2">
                                    <i class="fas fa-map-marker-alt text-oxo-red" aria-hidden="true"></i>
                                    Bureaux
                                </p>
                                <div class="flex flex-wrap gap-x-3 gap-y-1 pl-5">
                                    <a 
                                        v-for="location in partner.locations" 
                                        :key="location"
                                        :href="getMapsUrl(location, partner.name)"
                                        target="_blank"
                                        class="text-sm text-foreground hover:text-oxo-red transition-colors underline-offset-4 hover:underline decoration-oxo-red/50"
                                        @click.stop
                                    >
                                        {{ location }}
                                    </a>
                                </div>
                            </div>

                            <!-- FOR FREELANCE: Expertise -->
                            <div v-if="partner.type === 'Freelance' && partner.jobs && partner.jobs.length > 0">
                                <p class="text-[10px] font-semibold uppercase tracking-wider text-foreground-dim mb-1 flex items-center gap-2">
                                    <i class="fas fa-briefcase text-oxo-red" aria-hidden="true"></i>
                                    Expertise
                                </p>
                                <p class="text-sm text-foreground leading-relaxed pl-5">{{ partner.jobs.join(' • ') }}</p>
                            </div>

                            <!-- FOR COMPANY: Spécialités -->
                            <div v-if="partner.type === 'Company' && partner.jobs && partner.jobs.length > 0">
                                <p class="text-[10px] font-semibold uppercase tracking-wider text-foreground-dim mb-1 flex items-center gap-2">
                                    <i class="fas fa-gamepad text-oxo-red" aria-hidden="true"></i>
                                    Spécialités
                                </p>
                                <p class="text-sm text-foreground leading-relaxed pl-5">{{ partner.jobs.join(' • ') }}</p>
                            </div>
  
                            <!-- Partenaire depuis -->
                            <div v-if="partner.joinedDate">
                                <p class="text-[10px] font-semibold uppercase tracking-wider text-foreground-dim mb-1 flex items-center gap-2">
                                    <i class="fas fa-handshake text-oxo-red" aria-hidden="true"></i>
                                    Partenaire depuis
                                </p>
                                <p class="text-sm text-foreground pl-5">{{ getYear(partner.joinedDate) }}</p>
                            </div>
                        </div>
  
                        <div v-if="partner.website && partner.website.trim()" class="mt-auto space-y-2 pt-3 border-t border-white/5">
                            <a 
                              v-if="partner.website && partner.website.trim()" 
                              :href="partner.website" 
                              target="_blank"
                              class="flex items-center justify-center gap-2 w-full py-1.5 md:py-2.5 px-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 transition-all text-[11px] md:text-sm text-foreground group/btn"
                              @click.stop
                            >
                               <i class="fas fa-globe text-oxo-red group-hover/btn:scale-110 transition-transform" aria-hidden="true"></i>
                               Visiter le site web
                            </a>
                        </div>
                    </div>
                </div>
            </div>
          </article>
        </li>
      </UiCarousel>
    </div>

  </section>
</template>

<style scoped>
.perspective-1000 {
  perspective: 1000px;
}
.preserve-3d {
  transform-style: preserve-3d;
}
.backface-hidden {
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  -moz-backface-visibility: hidden;
  /* Firefox fix: force GPU layer */
  transform: translateZ(0);
}
.rotate-y-180 {
  transform: rotateY(180deg);
}

/* Firefox-specific fix for front face when flipped */
.rotate-y-180 .backface-hidden:first-child {
  visibility: hidden;
}
.rotate-y-180 .backface-hidden:last-child {
  visibility: visible;
}
</style>

